import React, { useEffect, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { api, fileHref } from "../lib/api";
import { cn } from "../lib/utils";

/**
 * Phase 86 — staff avatars everywhere a staff name appears.
 *
 * Names are denormalised across many records (collected_by_name, initiated_by_name, …), so
 * screens rarely carry a photo URL. A tiny per-tenant directory (GET /users/avatars) is
 * loaded once per session and shared by every screen through a module-level store;
 * `StaffAvatar` resolves the photo by user id first and by *unique* name as a fallback
 * (two staff with the same name never get each other's photo — that is the whole point).
 */
const stores = {};

function getStore(code) {
  if (!stores[code]) stores[code] = { byId: {}, byName: {}, loaded: false, promise: null, version: 0, listeners: new Set() };
  return stores[code];
}

function notify(store) {
  store.version += 1;
  store.listeners.forEach((fn) => fn(store.version));
}

export function loadAvatars(code, { force = false } = {}) {
  if (!code) return Promise.resolve(null);
  const store = getStore(code);
  if (store.promise && !force) return store.promise;
  store.promise = api.get(`/t/${code}/users/avatars`)
    .then((r) => {
      const byId = {}; const nameCount = {}; const byName = {};
      (r.data || []).forEach((u) => {
        byId[u.id] = u;
        const key = (u.name || "").trim().toLowerCase();
        if (key) { nameCount[key] = (nameCount[key] || 0) + 1; byName[key] = u; }
      });
      Object.keys(nameCount).forEach((k) => { if (nameCount[k] > 1) delete byName[k]; }); // ambiguous names never resolve
      store.byId = byId; store.byName = byName; store.loaded = true;
      notify(store);
      return store;
    })
    .catch(() => { store.loaded = true; notify(store); return store; });
  return store.promise;
}

/** Call after any photo change so every mounted avatar refreshes. */
export const refreshAvatars = (code) => loadAvatars(code, { force: true });

export function useAvatars(code) {
  const store = code ? getStore(code) : null;
  const [, setVersion] = useState(store ? store.version : 0);
  useEffect(() => {
    if (!store) return undefined;
    const fn = (v) => setVersion(v);
    store.listeners.add(fn);
    if (!store.promise) loadAvatars(code);
    return () => { store.listeners.delete(fn); };
  }, [code, store]);
  return store || { byId: {}, byName: {}, loaded: false };
}

/** Resolve the directory entry for (userId | name). */
export function resolveStaff(store, userId, name) {
  if (!store) return null;
  if (userId && store.byId[userId]) return store.byId[userId];
  const key = (name || "").trim().toLowerCase();
  return key ? store.byName[key] || null : null;
}

// ---- Phase 95: employee code beside every staff name ----
export const NO_CODE = "no code";

/**
 * Plain-string form used in selects, tooltips, toasts, CSV: "Ramesh Kumar (EMP-001)".
 * A staff member without a code prints "Ramesh Kumar (no code)" so admins notice and fill it in.
 * `unknown` = we could not resolve this person at all (no id, ambiguous name) → name only.
 */
export const staffLabelText = (name, empCode, { unknown = false, fallback = "—" } = {}) => {
  const n = (name || "").trim();
  if (!n) return fallback;
  const c = (empCode == null ? "" : String(empCode)).trim();
  if (c) return `${n} (${c})`;
  return unknown ? n : `${n} (${NO_CODE})`;
};

/**
 * Employee code for a staff reference: an explicit stamped code (e.g. collected_by_employee_id)
 * wins; otherwise the tenant directory is consulted by user id, then by *unique* name.
 * Returns { code, unknown } — `unknown` is true only when nobody could be resolved.
 */
export function resolveStaffCode(store, { userId, name, empCode } = {}) {
  const explicit = (empCode == null ? "" : String(empCode)).trim();
  if (explicit) return { code: explicit, unknown: false };
  const entry = resolveStaff(store, userId, name);
  if (entry) return { code: (entry.employee_id || "").trim(), unknown: false };
  return { code: "", unknown: true };
}

/** Hook form of resolveStaffCode for components that need the code as a string. */
export function useStaffCode(code, { userId, name, empCode } = {}) {
  const store = useAvatars(code);
  return resolveStaffCode(store, { userId, name, empCode });
}

/** Hook: full "Name (EMP-001)" string for a staff reference (selects, tooltips). */
export function useStaffLabel(code, { userId, name, empCode } = {}) {
  const r = useStaffCode(code, { userId, name, empCode });
  return staffLabelText(name, r.code, { unknown: r.unknown });
}

/**
 * The small monospace tag that follows a staff name. Copy/paste reads " (EMP-001)".
 * Missing code → amber "(no code)" so it stands out on lists; unresolvable → renders nothing.
 */
export const EmpCode = ({ code, unknown = false, className, testId }) => {
  const c = (code == null ? "" : String(code)).trim();
  if (!c && unknown) return null;
  return (
    <span className={cn("font-mono text-[11px] leading-none whitespace-nowrap",
      c ? "text-muted-foreground" : "text-amber-700/90 dark:text-amber-400/90", className)}
      data-testid={testId} data-emp-code={c || "none"} title={c ? `Employee code ${c}` : "No employee code on file — add one in Users & Staff"}>
      {" "}({c || NO_CODE})
    </span>
  );
};

/**
 * Text-only staff identity (no avatar) for dense tables, chips and inline sentences:
 * renders "Name (EMP-001)". Pass `userId` (preferred) and/or `name`; `empCode` when the record
 * already carries a stamped code (e.g. collected_by_employee_id).
 */
export const StaffLabel = ({ code, userId, name, empCode, fallback = "—", className, codeClassName, testId }) => {
  const store = useAvatars(code);
  const n = (name || resolveStaff(store, userId, null)?.name || "").trim();
  if (!n) return <span className={className} data-testid={testId}>{fallback}</span>;
  const r = resolveStaffCode(store, { userId, name: n, empCode });
  return (
    <span className={cn("min-w-0", className)} data-testid={testId} data-staff-id={userId || undefined}>
      {n}
      <EmpCode code={r.code} unknown={r.unknown} className={codeClassName} />
    </span>
  );
};

// ---- visuals ----
const SIZES = {
  xs: "h-6 w-6 text-[10px]", sm: "h-8 w-8 text-xs", md: "h-10 w-10 text-sm",
  lg: "h-16 w-16 text-xl", xl: "h-24 w-24 text-3xl",
};
// hues drawn from the app palette (primary blue, success green, info, warning amber, plus two neutrals)
const HUES = [206, 152, 199, 38, 262, 330];

export const initialsOf = (name) => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const avatarHue = (key) => {
  let h = 0;
  for (const ch of String(key || "")) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return HUES[h % HUES.length];
};

export const avatarColors = (key) => {
  const hue = avatarHue(key);
  return { bg: `hsl(${hue} 45% 90%)`, fg: `hsl(${hue} 55% 28%)`, ring: `hsl(${hue} 55% 45%)` };
};

/**
 * Circular staff avatar with initials fallback.
 * Provide `photoUrl` when the record has it; otherwise pass `userId` and/or `name` and it is
 * resolved from the tenant directory. `size`: xs | sm | md | lg | xl.
 */
export const StaffAvatar = ({ code, userId, name, photoUrl, size = "sm", className, title, testId }) => {
  const store = useAvatars(code);
  const entry = resolveStaff(store, userId, name);
  const url = photoUrl !== undefined && photoUrl !== null ? photoUrl : entry?.photo_url;
  const label = name || entry?.name || "";
  const colors = avatarColors(userId || entry?.id || label);
  return (
    <Avatar className={cn(SIZES[size] || SIZES.sm, "shrink-0 ring-1 ring-border", className)} title={title ?? label}
      data-testid={testId} data-has-photo={url ? "true" : "false"}>
      {url && <AvatarImage src={url.startsWith("data:") ? url : fileHref(url)} alt={label} className="object-cover" />}
      <AvatarFallback className="font-semibold select-none" style={{ background: colors.bg, color: colors.fg }} aria-label={label}>
        {initialsOf(label)}
      </AvatarFallback>
    </Avatar>
  );
};

/** Avatar + name (+ employee code tag, + optional sub-line) inline — the drop-in replacement for a bare name. */
export const StaffName = ({ code, userId, name, photoUrl, empCode, showCode = true, sub, size = "sm", className, nameClassName, subClassName, testId, children }) => {
  const store = useAvatars(code);
  const r = showCode ? resolveStaffCode(store, { userId, name, empCode }) : null;
  return (
    <span className={cn("inline-flex items-center gap-2 min-w-0", className)} data-testid={testId}>
      <StaffAvatar code={code} userId={userId} name={name} photoUrl={photoUrl} size={size} />
      <span className="min-w-0">
        <span className={cn("block truncate", nameClassName)}>
          {name || "—"}
          {r && name ? <EmpCode code={r.code} unknown={r.unknown} /> : null}
        </span>
        {sub && <span className={cn("block text-[11px] text-muted-foreground truncate", subClassName)}>{sub}</span>}
        {children}
      </span>
    </span>
  );
};

/**
 * HTML for a Leaflet divIcon marker: circular photo (or initials) with a coloured ring and a
 * small pointer, so Agent Tracking pins show *who* is where.
 * `dot` (Phase 87): optional presence colour drawn at the bottom-right of the circle
 * (e.g. green = logged in today, red = not logged in).
 */
export function avatarMarkerHtml({ id, name, photoUrl, ringColor, size = 38, live = true, dot = null, dotTitle = "" }) {
  const colors = avatarColors(id || name);
  const ring = ringColor || colors.ring;
  const inner = photoUrl
    ? `<img src="${fileHref(photoUrl)}" alt="" style="width:100%;height:100%;object-fit:cover;display:block" />`
    : `<span style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;background:${colors.bg};color:${colors.fg};font:600 ${Math.round(size * 0.36)}px/1 Figtree,Montserrat,sans-serif;">${initialsOf(name)}</span>`;
  const dotSize = Math.max(10, Math.round(size * 0.3));
  const presence = dot
    ? `<span title="${dotTitle}" data-presence-dot="${dot}" style="position:absolute;right:-2px;bottom:6px;width:${dotSize}px;height:${dotSize}px;border-radius:50%;background:${dot};border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.35);"></span>`
    : "";
  return `
    <div style="position:relative;width:${size}px;height:${size + 8}px;">
      <div style="width:${size}px;height:${size}px;border-radius:50%;overflow:hidden;border:3px solid ${ring};box-shadow:0 2px 6px rgba(0,0,0,.35);background:#fff;${live ? "" : "filter:grayscale(1);opacity:.75;"}">${inner}</div>
      <div style="position:absolute;left:50%;bottom:0;transform:translateX(-50%);width:0;height:0;border-left:6px solid transparent;border-right:6px solid transparent;border-top:8px solid ${ring};"></div>
      ${presence}
    </div>`;
}
